import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import StudentFollowUp from "./studentFollowUp/StudentFollowUp";

export default function PerformanceTracker() {
  const [activeTab, setActiveTab] = useState("video");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [modalTab, setModalTab] = useState("performance");

  // ---------- Fetch main list ----------
  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/getPerformanceList`,
        { params: { startDate, endDate } }
      );
      if (res.data.success) {
        setData(res.data.data || []);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("Failed to fetch performance list:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "video") {
        fetchList(); // first load

        // auto refresh every 30 seconds
        const timer = setInterval(() => {
        fetchList();
        }, 60000);

        return () => clearInterval(timer);
    } else {
        setData([]);
    }
  }, [activeTab, startDate, endDate]);

  // ---------- Fetch student detail ----------
  const openStudentModal = async (student) => {
    setSelectedStudent(student);
    setModalTab("performance");
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin/getStudentPerformanceDetail/${student.student_id}`
      );
      if (res.data.success) {
        setDetail(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch student detail:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  // ---------- Check-in helper ----------
  const getCheckinDisplay = (row) => {
    const bookings = row.today_bookings || [];
    if (bookings.length === 0) {
      return { time: null, isArrived: false, isLate: false, color: "#f3f4f6" };
    }

    const earliest = bookings.reduce((prev, curr) =>
      prev.start_time < curr.start_time ? prev : curr
    );
    const time = earliest.start_time;

    const isArrived = bookings.some((b) => b.booking_status === "completed");

    let isLate = false;
    if (!isArrived && time) {
      const now = new Date();
      const [h, m] = time.split(":").map(Number);
      const start = new Date();
      start.setHours(h, m, 0, 0);
      const diffMinutes = (now - start) / (1000 * 60);
      if (diffMinutes >= 60) isLate = true;
    }

    let color = "#fef9c3"; // yellow
    if (isArrived) color = "#dcfce7"; // green
    if (isLate) color = "#fee2e2"; // red

    return { time, isArrived, isLate, color };
  };

  // ---------- Filtering ----------
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const keyword = search.toLowerCase();
      return (
        item.student_name?.toLowerCase().includes(keyword) ||
        item.student_id?.toLowerCase().includes(keyword)
      );
    });
  }, [data, search]);

  // ---------- Sorting + Default (not arrived first) ----------
  const sortedData = useMemo(() => {
    let sortable = [...filteredData];

    // Default: not arrived students first
    if (!sortConfig.key) {
      sortable.sort((a, b) => {
        const aArrived = getCheckinDisplay(a).isArrived;
        const bArrived = getCheckinDisplay(b).isArrived;
        // false (not arrived) comes before true (arrived)
        return Number(aArrived) - Number(bArrived);
      });
      return sortable;
    }

    // Custom column sort
    sortable.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // special handling for check-in related sort
      if (sortConfig.key === "checkin") {
        aVal = getCheckinDisplay(a).isArrived ? 1 : 0;
        bVal = getCheckinDisplay(b).isArrived ? 1 : 0;
      }

      if (aVal === null || aVal === undefined) aVal = "";
      if (bVal === null || bVal === undefined) bVal = "";

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sortable;
  }, [filteredData, sortConfig]);

  // ---------- Group by check-in time ----------
  const groupedData = useMemo(() => {
    const groups = {};

    sortedData.forEach((row) => {
      const { time } = getCheckinDisplay(row);
      const key = time || "No Time";

      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });

    // Sort the time keys (No Time goes last)
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      if (a === "No Time") return 1;
      if (b === "No Time") return -1;
      return a.localeCompare(b);
    });

    return sortedKeys.map((time) => ({
      time,
      students: groups[time],
    }));
  }, [sortedData]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  // ---------- Test score helper ----------
  const formatTest = (row) => {
    if (row.test_mark === null || row.test_mark === undefined) {
      return { text: "—", color: "#f3f4f6" };
    }
    return {
      text: `${row.test_mark} (${row.test_percentile ?? "—"}%)`,
      color: row.test_color || "#f3f4f6",
    };
  };

  // ---------- Colored cell ----------
  const ColorCell = ({ value, color }) => (
    <td className="px-4 py-3 text-sm">
      <span
        className="inline-block px-2.5 py-1 rounded-md font-medium text-gray-800"
        style={{ backgroundColor: color || "#f3f4f6" }}
      >
        {value ?? "—"}
      </span>
    </td>
  );

  return (
    <div className="w-full h-full min-h-screen bg-white flex flex-col p-6 overflow-hidden box-border rounded-xl shadow-sm">
      {/* Title */}
      <div className="mb-6 pb-4 border-b border-gray-200 shrink-0">
        <h1 className="text-3xl font-extrabold text-neutral-900 tracking-tight">
          Performance Tracker
        </h1>
      </div>

      {/* Tabs + Date Range + Search */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 shrink-0">
        <div className="flex bg-gray-100 p-1 rounded-lg border border-gray-200 w-fit">
          <button
            onClick={() => setActiveTab("live")}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === "live"
                ? "bg-white text-neutral-900 shadow-sm"
                : "text-neutral-600 hover:text-neutral-900"
            }`}
          >
            Live
          </button>
          <button
            onClick={() => setActiveTab("video")}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === "video"
                ? "bg-white text-neutral-900 shadow-sm"
                : "text-neutral-600 hover:text-neutral-900"
            }`}
          >
            Video
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <input
            type="text"
            placeholder="Search student..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* ==================== SCROLLABLE TABLE ==================== */}
      <div className="flex-1 min-h-0 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm flex flex-col">
        <div className="overflow-auto flex-1">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th
                  className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50"
                  onClick={() => handleSort("checkin")}
                >
                  Check-in Status{getSortIndicator("checkin")}
                </th>
                <th
                  className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50"
                  onClick={() => handleSort("student_name")}
                >
                  Student{getSortIndicator("student_name")}
                </th>
                <th
                  className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50"
                  onClick={() => handleSort("attendance_days")}
                >
                  Attendance{getSortIndicator("attendance_days")}
                </th>
                <th
                  className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50"
                  onClick={() => handleSort("homework_status")}
                >
                  Homework Status{getSortIndicator("homework_status")}
                </th>
                <th
                  className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50"
                  onClick={() => handleSort("test_mark")}
                >
                  Test Score{getSortIndicator("test_mark")}
                </th>
                <th
                  className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50"
                  onClick={() => handleSort("action_badge")}
                >
                  Action Badge{getSortIndicator("action_badge")}
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500 text-sm">
                    Loading...
                  </td>
                </tr>
              ) : activeTab === "live" ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500 text-sm">
                    Live performance tracking is coming soon.
                  </td>
                </tr>
              ) : groupedData.length > 0 ? (
                groupedData.map((group) => (
                  <React.Fragment key={group.time}>
                    {/* Group Header */}
                    <tr className="bg-slate-100">
                      <td colSpan={6} className="px-4 py-2.5 text-sm font-bold text-slate-700">
                        {group.time === "No Time"
                          ? "No Check-in Time"
                          : `Check-in Time: ${group.time}`}
                        <span className="ml-2 text-xs font-normal text-slate-500">
                          ({group.students.length} student
                          {group.students.length > 1 ? "s" : ""})
                        </span>
                      </td>
                    </tr>

                    {/* Students */}
                    {group.students.map((row) => {
                      const checkin = getCheckinDisplay(row);
                      const testDisplay = formatTest(row);

                      return (
                        <tr
                          key={row.student_id}
                          onClick={() => openStudentModal(row)}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          {/* Check-in Status */}
                          <td className="px-4 py-3 text-sm">
                            {checkin.time ? (
                              <span
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md font-medium text-gray-800"
                                style={{ backgroundColor: checkin.color }}
                              >
                                <span>{checkin.time}</span>
                                {checkin.isArrived && (
                                  <span className="text-[10px] font-semibold uppercase tracking-wide opacity-80">
                                    arrived
                                  </span>
                                )}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>

                          {/* Student */}
                          <td className="px-4 py-3 text-sm font-medium text-neutral-800">
                            {row.student_name}
                            <div className="text-xs text-gray-500">{row.student_id}</div>
                          </td>

                          {/* Attendance */}
                          <ColorCell
                            value={
                              row.attendance_days !== null &&
                              row.attendance_days !== undefined
                                ? `${row.attendance_days} days`
                                : "—"
                            }
                            color={row.attendance_color}
                          />

                          {/* Homework */}
                          <ColorCell
                            value={row.homework_status}
                            color={row.homework_color}
                          />

                          {/* Test Score */}
                          <ColorCell
                            value={testDisplay.text}
                            color={testDisplay.color}
                          />

                          {/* Action Badge */}
                          <ColorCell
                            value={row.action_badge || "—"}
                            color={row.action_color}
                          />
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500 text-sm">
                    No records found for the selected date range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ==================== MODAL ==================== */}
      {selectedStudent && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-bold text-neutral-900">
                  {selectedStudent.student_name}
                </h2>
                <p className="text-sm text-gray-500">{selectedStudent.student_id}</p>
              </div>
              <button
                onClick={() => {
                  setSelectedStudent(null);
                  setDetail(null);
                  setModalTab("performance");
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="px-6 pt-4 flex gap-3 border-b border-gray-100">
              <button
                onClick={() => setModalTab("performance")}
                className={`px-4 py-2 text-sm font-medium border-b-2 ${
                  modalTab === "performance"
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-gray-500"
                }`}
              >
                Performance Tracker
              </button>
              <button
                onClick={() => setModalTab("follow-up")}
                className={`px-4 py-2 text-sm font-medium border-b-2 ${
                  modalTab === "follow-up"
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-gray-500"
                }`}
              >
                Student Follow Up
              </button>
            </div>

            <div className="p-6">
              {modalTab === "follow-up" ? (
                <StudentFollowUp studentId={selectedStudent.student_id} />
              ) : detailLoading ? (
                <p className="text-center text-gray-500 py-10">Loading student details...</p>
              ) : !detail ? (
                <p className="text-center text-gray-500 py-10">Failed to load details.</p>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Attendance */}
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                      Attendance History
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Average visit interval:{" "}
                      <span className="font-semibold text-neutral-800">
                        {detail.attendance.average_interval
                          ? `${detail.attendance.average_interval} days`
                          : "N/A"}
                      </span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {detail.attendance.color_boxes?.length > 0 ? (
                        detail.attendance.color_boxes.map((box, idx) => (
                          <div
                            key={idx}
                            className="w-8 h-8 rounded-md"
                            style={{ backgroundColor: box.color }}
                            title={`${box.from} → ${box.to} (${box.days} days)`}
                          />
                        ))
                      ) : (
                        <p className="text-xs text-gray-400">No history</p>
                      )}
                    </div>
                  </div>

                  {/* Homework */}
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                      Homework
                    </h3>
                    <p className="text-sm text-gray-700 mb-4">
                      Submitted: {detail.homework.summary.submitted} || Late-Submission:{" "}
                      {detail.homework.summary.late_submission} || Pending:{" "}
                      {detail.homework.summary.pending}
                    </p>
                    <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                      {detail.homework.records?.length > 0 ? (
                        detail.homework.records.map((rec, idx) => (
                          <div
                            key={idx}
                            className="w-8 h-8 rounded-md"
                            style={{ backgroundColor: rec.color }}
                            title={`Due lesson: ${rec.due_lesson ?? "N/A"} | ${rec.due_lesson_day ?? "N/A"} lesson(s) after due | ${rec.status}`}
                          />
                        ))
                      ) : (
                        <p className="text-xs text-gray-400">No homework records</p>
                      )}
                    </div>
                  </div>

                  {/* Test */}
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                      Test Timeline
                    </h3>
                    <p className="text-sm text-gray-700 mb-4">
                      Average Percentile:{" "}
                      <span className="font-semibold">
                        {detail.test.average_percentile !== null
                          ? `${detail.test.average_percentile}%`
                          : "N/A"}
                      </span>
                    </p>
                    <div className="space-y-3 max-h-48 overflow-y-auto pr-1">
                      {detail.test.records?.length > 0 ? (
                        detail.test.records.map((t, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between text-sm bg-white rounded-lg px-3 py-2 border"
                          >
                            <div>
                              <p className="font-medium">{t.test_name}</p>
                              <p className="text-xs text-gray-500">
                                {t.submission_date?.slice(0, 10)} • {t.test_mark} marks
                              </p>
                            </div>
                            <span
                              className="px-2 py-0.5 rounded text-xs font-medium"
                              style={{ backgroundColor: t.color }}
                            >
                              {t.percentile !== null ? `${t.percentile}%` : "—"}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-gray-400">No test records</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}